import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { constantsData, statusTypesData } from "@/data";
import { getTrackQuery, updateVideoMutation } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useEditVideo = (trackId) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [updateVideoAction, { loading }] = useMutation(updateVideoMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getTrackQuery,
                variables: {
                    where: { id: trackId },
                },
            },
        ],
    });

    const editVideoIsrc = async (id, isrc, onError) => {
        try {
            const { data } = await updateVideoAction({
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.editMsgs.success", { entity: t("videos.entity") }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    data: {
                        isrc: { set: isrc },
                    },
                    where: { id },
                },
            });

            return !!data?.updateOneVideo;
        } catch (err) {
            if (onError) onError(err);

            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );

            return false;
        }
    };

    // an image id makes it the active thumbnail; null clears it so distribution falls back to the auto-generated one
    const editVideoThumbnail = async (id, imageId, { successMessage, withoutAlert } = {}) => {
        try {
            const { data } = await updateVideoAction({
                onCompleted: () => {
                    if (!withoutAlert) {
                        dispatch(
                            openAlert(
                                successMessage || t("messages.editMsgs.success", { entity: t("videos.thumbnail") }),
                                statusTypesData.success,
                            ),
                        );
                    }
                },
                variables: {
                    data: {
                        thumbnail: imageId ? { connect: { id: imageId } } : { disconnect: true },
                    },
                    where: { id },
                },
            });

            return !!data?.updateOneVideo;
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch
                    ? t("messages.networkFailure")
                    : `${t("messages.editMsgs.fail", { entity: t("videos.thumbnail") })}, ${err.message}`,
                dispatch,
                openAlert,
            );

            return false;
        }
    };

    return { editVideoIsrc, editVideoThumbnail, loading };
};

export default useEditVideo;
